import dotProp from 'dot-prop'
import { Subscription } from '../types/subscription'
import { extractDateRangeFromUrl } from '../util/helpers'
import { UserState } from '../types/reducers/user'
import {
    CLEAR_CURRENT_USER,
    REQUEST_USER,
    SET_CURRENT_PROFILE,
    SET_CURRENT_USER,
    SET_DATE_RANGE,
    SET_HIDDEN_COLUMNS,
    SET_NAV_OPEN,
    SET_SCREENSHOT_MODE,
    SET_URL_SEARCH_PARAMS,
    SetCurrentUserAction,
} from '../constants/reducers/user'
import { Action } from '../constants/reducers/state'
import type { Models } from '../types/profile'
import { userStore } from '../stores/user_store'
import { type EntityId, idKey } from '../types/constants'

export const resolveInitialSearchQuery = (): string => {
    const search = window.location.search
    if (!search) {
        return ''
    }

    const searchObject = new URLSearchParams(search)
    return searchObject.toString()
}

export const UserInitialState: UserState = {
    current: null,
    fetching: false,
    loaded: false,
    profile: null,
    field_visibility: null,
    screenshot_mode: false,
    active_subscription: null,
    has_active_subscription: false,
    date_range: { ...extractDateRangeFromUrl() },
    nav_open: true,
    search_params: resolveInitialSearchQuery(),
}

export const userReducer = (state: UserState, action: Action): UserState => {
    if (typeof state === 'undefined') {
        return UserInitialState
    }

    const sp = new URLSearchParams(state.search_params)

    switch (action.type) {
        case SET_CURRENT_USER:
            // If the profile id and table settings already exist, then use those.
            // Otherwise, pull them fresh from the passed user object
            const requestedProfileId = sp.get('profileId')
            const profileIdFromQuery = action.user.keys?.profiles?.find(
                (profile) => idKey(profile.profile_id) === requestedProfileId,
            )?.profile_id
            const profileId = profileIdFromQuery ?? resolveProfile(state, action) ?? null
            const tableSettings =
                state.field_visibility ||
                (action.user.keys ? action.user.keys.profiles[0]?.field_visibility : undefined)
            const activeSubscription = action.user.active_subscription
                ? new Subscription(action.user.active_subscription)
                : null

            if (profileId) {
                window.axios.defaults.headers.common['ProfileId'] = profileId
            } else {
                delete window.axios.defaults.headers.common['ProfileId']
            }

            if (action.user.keys?.profiles) {
                action.user.keys.profiles = action.user.keys.profiles.sort(
                    (a: Models.Profile, b: Models.Profile) => {
                        if (a.total_ad_spend_30_days > b.total_ad_spend_30_days) {
                            return -1
                        } else if (a.total_ad_spend_30_days < b.total_ad_spend_30_days) {
                            return 1
                        }
                        return 0
                    },
                )
            }

            userStore.setUser(action.user)
            userStore.setProfileId(profileId)

            return Object.assign({}, state, {
                current: action.user,
                fetching: false,
                loaded: true,
                profile: profileId,
                field_visibility: tableSettings,
                active_subscription: activeSubscription,
                has_active_subscription: !!activeSubscription,
            })
        case CLEAR_CURRENT_USER:
            delete window.axios.defaults.headers.common['ProfileId']

            userStore.clearUser()

            return Object.assign({}, state, {
                current: undefined,
                fetching: false,
                loaded: true,
                profile: null,
                field_visibility: null,
                active_subscription: false,
            })
        case REQUEST_USER:
            return Object.assign({}, state, {
                fetching: true,
            })
        case SET_CURRENT_PROFILE:
            window.axios.defaults.headers.common['ProfileId'] = action.profile

            sp.set('profileId', String(action.profile))

            userStore.setProfileId(action.profile)

            return Object.assign({}, state, {
                profile: action.profile,
                search_params: sp.toString(),
            })
        case SET_HIDDEN_COLUMNS:
            console.log('Action is', action)
            const profile = state.profile
            const profiles: Models.Profile[] = state.current?.keys?.profiles || []
            // MER-2802: profile_id may arrive as a number or a string. Compare on String(...).
            const activeProfileIndex: number | null = profiles?.findIndex(
                (p) => idKey(p.profile_id) === idKey(profile),
            )
            const activeProfile: Models.Profile | null = profiles[activeProfileIndex]

            const fieldIndex: number | null = activeProfile?.field_visibility.findIndex(
                (fv) => fv.table_name === action.name,
            )

            if (fieldIndex === -1) {
                // Push new object
                const path = `current.keys.profiles.${activeProfileIndex}.field_visibility`
                const list: any[] = dotProp.get(state, path) ?? []
                return dotProp.set(state, path, [
                    ...list,
                    {
                        hidden_columns: action.columns,
                        table_name: action.name,
                        profile_id: profile,
                    },
                ])
            }

            // Update existing hidden_columns
            const path = `current.keys.profiles.${activeProfileIndex}.field_visibility.${fieldIndex}.hidden_columns`
            return dotProp.set(state, path, [...action.columns])
        case SET_SCREENSHOT_MODE:
            state.screenshot_mode = !state.screenshot_mode
            return state
        case SET_DATE_RANGE:
            // state.date_range = action.dateRange
            console.log('SET DATE RANGE FIRED')
            console.log('ACTION IS', action, state)
            return state
        case SET_NAV_OPEN:
            state.nav_open = action.open
            return state
        case SET_URL_SEARCH_PARAMS:
            console.log('SET_URL_SEARCH_PARAMS', action.searchParams)
            state.search_params = action.searchParams
            return state
        default:
            return state
    }
}

const resolveProfile = (state: UserState, action: SetCurrentUserAction): EntityId | undefined => {
    const profiles = action.user.keys?.profiles ?? []
    let profile = state.profile ?? resolveDefaultProfile(action)

    // A user refresh can remove access to the previously selected profile. Never retain a
    // ProfileId header that is absent from the newly authorized profile list.
    // MER-2802: profile_id may arrive as a number or a string. Compare on String(...).
    let selectedProfile = profiles.find((p) => idKey(p.profile_id) === idKey(profile))
    if (!selectedProfile) {
        profile = resolveDefaultProfile(action)
        selectedProfile = profiles.find((p) => idKey(p.profile_id) === idKey(profile))
    }

    // If the currently selected profile is not managed, then fallback to the next managed profile.
    if (selectedProfile?.managed === false) {
        profile = profiles.find((p) => p.managed === true)?.profile_id
    }

    return profile
}

const resolveDefaultProfile = (action: SetCurrentUserAction): EntityId | undefined => {
    const profiles = action.user.keys?.profiles
    if (!profiles || profiles.length === 0) {
        return undefined
    }

    const configuredDefault = profiles.find((profile) => profile.settings?.default_profile != null)
        ?.settings?.default_profile
    const defaultProfile = profiles.find(
        (profile) => idKey(profile.profile_id) === idKey(configuredDefault),
    )

    return defaultProfile?.profile_id ?? profiles[0].profile_id
}
